import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { GridLayout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { ArrowRight, AlertTriangle, Info, AlertCircle, LayoutGrid, Check, RotateCcw, Plus, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { KpiCard, Panel, StatusBadge, LoadingState, CCButton } from "@/command/ccUi";

const SEV_ICON = { info: Info, warning: AlertTriangle, critical: AlertCircle };

const PANEL_META = {
  "panel:intelligence": "Intelligence signals",
  "panel:activity": "Recent activity",
  "panel:health": "ORAYA health",
};

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

export default function Overview() {
  const { boot } = useCommand();
  const [dash, setDash] = useState(null);
  const [intel, setIntel] = useState(null);
  const [activity, setActivity] = useState([]);
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const containerRef = useRef(null);
  const [width, setWidth] = useState(1200);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => { for (const e of entries) setWidth(e.contentRect.width); });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [loading]);

  const load = useCallback(async () => {
    try {
      const [d, i, a, l] = await Promise.all([
        api.get("/cc/dashboard"),
        api.get("/cc/intelligence").catch(() => ({ data: null })),
        api.get("/cc/activity"),
        api.get("/cc/dashboard/layout"),
      ]);
      setDash(d.data); setIntel(i.data); setActivity(a.data); setLayout(l.data.layout);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime(["activity", "approvals", "company", "governance", "users", "notification", "finance", "people", "sales", "customers", "compliance"], load);

  const kpiByKey = useMemo(() => Object.fromEntries((dash?.kpis || []).map((k) => [k.key, k])), [dash]);
  const visibleIds = useMemo(() => (layout || []).map((w) => w.i), [layout]);
  const allIds = useMemo(() => {
    const kpis = (dash?.kpis || []).map((k) => `kpi:${k.key}`);
    return [...kpis, ...Object.keys(PANEL_META)];
  }, [dash]);
  const addableIds = allIds.filter((id) => !visibleIds.includes(id));

  const labelFor = (id) => id.startsWith("kpi:") ? (kpiByKey[id.slice(4)]?.label || id.slice(4)) : (PANEL_META[id] || id);

  const save = async (nextLayout) => {
    try { await api.put("/cc/dashboard/layout", { layout: nextLayout }); }
    catch { toast.error("Could not save layout"); }
  };
  const onLayoutChange = (l) => { if (editing) { const cleaned = l.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })); setLayout(cleaned); } };
  const finishEditing = async () => { setEditing(false); await save(layout); toast.success("Dashboard saved"); };
  const removeWidget = (id) => setLayout((l) => l.filter((w) => w.i !== id));
  const addWidget = (id) => {
    const isPanel = id.startsWith("panel:");
    const maxY = layout.reduce((m, w) => Math.max(m, w.y + w.h), 0);
    setLayout((l) => [...l, { i: id, x: 0, y: maxY, w: isPanel ? 6 : 3, h: isPanel ? 5 : 2 }]);
  };
  const reset = async () => {
    const { data } = await api.post("/cc/dashboard/layout/reset");
    setLayout(data.layout); toast.success("Dashboard reset");
  };

  if (loading || !layout) return <LoadingState label="Loading ORAYA…" />;

  const renderWidget = (id) => {
    if (id.startsWith("kpi:")) {
      const k = kpiByKey[id.slice(4)];
      if (!k) return <div className="glass-heavy rounded-2xl p-5 h-full text-subtle text-sm">Unavailable</div>;
      return <div className="h-full"><KpiCard label={k.label} value={k.value} prefix={k.prefix || ""} empty={k.empty} /></div>;
    }
    if (id === "panel:intelligence") return (
      <Panel title="ORAYA Intelligence" className="h-full overflow-auto" testid="intelligence-panel"
        action={<span className="text-xs text-subtle">{intel?.count || 0} require attention</span>}>
        {(!intel || intel.count === 0) ? <p className="text-subtle text-sm py-6 text-center">Nothing needs your attention right now.</p> : (
          <div className="flex flex-col gap-2">
            {intel.alerts.map((a, i) => {
              const Icon = SEV_ICON[a.severity] || Info;
              return (
                <Link key={i} to={a.link || "#"} data-testid={`intel-alert-${i}`} className="flex items-center gap-3 p-3 rounded-xl glass-medium hover:border-hairline/25 transition-colors">
                  <Icon className={`w-4 h-4 ${a.severity === "critical" ? "text-red-500" : a.severity === "warning" ? "text-amber-500" : "text-oraya-accent"}`} />
                  <span className="text-sm text-ink flex-1">{a.text}</span>
                  <StatusBadge status={a.severity} label={a.source} />
                </Link>
              );
            })}
          </div>
        )}
      </Panel>
    );
    if (id === "panel:activity") return (
      <Panel title="Recent Activity" className="h-full overflow-auto" testid="activity-panel">
        {activity.length === 0 ? <p className="text-subtle text-sm py-6 text-center">No activity yet.</p> : (
          <div className="flex flex-col gap-3">
            {activity.map((a) => (
              <div key={a.id} className="text-sm" data-testid="activity-item">
                <p className="text-ink">{a.text}</p>
                <p className="text-[11px] text-subtle mt-0.5">{new Date(a.created_at).toLocaleString()}{a.user_email ? ` · ${a.user_email}` : ""}</p>
              </div>
            ))}
          </div>
        )}
      </Panel>
    );
    if (id === "panel:health") return (
      <Panel title="ORAYA Health" className="h-full overflow-auto" testid="health-panel">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {dash?.health && Object.entries(dash.health).map(([name, hh]) => (
            <div key={name} className="glass-medium rounded-2xl p-4" data-testid={`health-${name.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm text-ink">{name}</span>
                <StatusBadge status={hh.status} label={hh.status === "insufficient" ? "No data" : hh.status} />
              </div>
              <p className="display text-2xl font-light text-ink mt-2">{hh.score == null ? "—" : hh.score}</p>
              <p className="text-xs text-subtle mt-1">{hh.reason}</p>
            </div>
          ))}
        </div>
      </Panel>
    );
    return null;
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <h1 className="display text-2xl md:text-3xl font-light text-ink">{greeting()}. Here's ORAYA.</h1>
          <p className="text-subtle mt-1">Live company overview{boot?.user?.name ? `, ${boot.user.name.split(" ")[0]}` : ""}.</p>
        </div>
        <div className="flex items-center gap-2">
          {editing && <CCButton variant="ghost" onClick={reset} testid="dash-reset"><RotateCcw className="w-4 h-4" /> Reset</CCButton>}
          {editing ? (
            <CCButton onClick={finishEditing} testid="dash-done"><Check className="w-4 h-4" /> Done</CCButton>
          ) : (
            <CCButton variant="ghost" onClick={() => setEditing(true)} testid="dash-customize"><LayoutGrid className="w-4 h-4" /> Customize</CCButton>
          )}
        </div>
      </div>

      {editing && (
        <div className="glass-medium rounded-2xl p-4 mb-6" data-testid="dash-widget-tray">
          <p className="text-xs uppercase tracking-[0.16em] text-subtle mb-3">Drag to move · drag corner to resize · add widgets</p>
          {addableIds.length === 0 ? <p className="text-subtle text-sm">All widgets are on your dashboard.</p> : (
            <div className="flex flex-wrap gap-2">
              {addableIds.map((id) => (
                <button key={id} onClick={() => addWidget(id)} data-testid={`dash-add-${id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs glass-medium text-ink hover:border-hairline/25">
                  <Plus className="w-3 h-3" /> {labelFor(id)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div ref={containerRef}>
        <GridLayout className="layout" layout={layout} cols={12} rowHeight={64} margin={[16, 16]} width={width}
          isDraggable={editing} isResizable={editing} draggableHandle=".drag-handle" onLayoutChange={onLayoutChange}>
          {layout.map((w) => (
            <div key={w.i} className="relative">
              {editing && (
                <>
                  <div className="drag-handle absolute -top-2 -left-2 z-10 w-7 h-7 rounded-full bg-invert text-invert-ink flex items-center justify-center cursor-move shadow" title="Drag"><GripVertical className="w-3.5 h-3.5" /></div>
                  <button onClick={() => removeWidget(w.i)} data-testid={`dash-remove-${w.i}`} className="absolute -top-2 -right-2 z-10 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center shadow" title="Remove"><X className="w-3.5 h-3.5" /></button>
                  <div className="absolute inset-0 rounded-2xl ring-1 ring-oraya-accent/30 pointer-events-none" />
                </>
              )}
              {renderWidget(w.i)}
            </div>
          ))}
        </GridLayout>
      </div>
    </div>
  );
}
